<section [formGroup]="group()" class="grid gap-8">
  <!-- The underlying problems, picked from the full list, and their impact on the client -->
  <div>
    <h2 class="mb-1 text-lg font-semibold text-slate-900">Underlying problems</h2>
    <p class="mb-4 text-sm text-slate-500">
      Problems behind the presenting one that affect the scope of EAP counselling. Choose any number from the full list, or "Not applicable".
    </p>
    <div class="grid gap-4 md:grid-cols-2">
      <mat-form-field appearance="outline" class="md:col-span-2">
        <mat-label>Underlying problems</mat-label>
        <mat-select multiple formControlName="underlyingProblemIds" (selectionChange)="underlyingProblemsChanged($event.value)">
          @for (item of notApplicableProblems(); track item.id) {
            <mat-option [value]="item.id">{{ item.name ?? item.code }}</mat-option>
          }
          @for (group of underlyingProblemGroups(); track group.name) {
            <mat-optgroup [label]="group.name">
              @for (item of group.problems; track item.id) {
                <mat-option [value]="item.id">{{ item.name ?? item.code }}</mat-option>
              }
            </mat-optgroup>
          }
        </mat-select>
      </mat-form-field>
    </div>
    <div class="mt-2">
      <scale-slider
        label="Impact of the underlying problem on the client"
        formControlName="functionalImpactSeverityId"
        [options]="options('functionalImpactSeverities')"
        [showDescription]="true" />
    </div>
  </div>

  <!-- One compact slider for each kind of impact on work; a slider left alone is not saved -->
  <div>
    <h2 class="mb-1 text-lg font-semibold text-slate-900">Impact of the presenting problem on work performance</h2>
    <p class="mb-4 text-sm text-slate-500">Where it applies. Rate each from 1 to 5, with 5 the biggest impact. Leave out what does not apply.</p>
    <div formArrayName="workPerformanceImpactRatings" class="grid gap-x-8 gap-y-5 md:grid-cols-2">
      @for (row of workRows().controls; track $index; let index = $index) {
        <div [formGroupName]="index">
          <scale-slider
            [label]="lookupName('workPerformanceImpactTypes', row.value.workPerformanceImpactTypeId)"
            formControlName="impactRating"
            [options]="options('severityScales')"
            [numeric]="true"
            [minValue]="1"
            [clearable]="true" />
        </div>
      }
    </div>
  </div>

  <!-- The screenings that were done; ticking one opens its result and notes -->
  <div [formGroup]="group()">
    <h2 class="mb-1 text-lg font-semibold text-slate-900">Risk assessment</h2>
    <p class="mb-3 text-sm text-slate-500">Tick each screening that was done. Once it is ticked, its result and notes can be written down.</p>
    @if (riskScreener(); as screener) {
      <p class="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
        The Suicide Risk Screener (MW19) was completed on {{ screener.screenedDate }}: {{ screener.riskRatingName }}.
      </p>
    }
    <cia-checklist
      [items]="screenings()"
      [rows]="screeningRows()"
      typeField="riskScreeningTypeId"
      [createRow]="createScreening"
      detail="result-notes"
      layout="grid"
      [label]="screeningLabel"
      [readOnly]="readOnly()" />
  </div>

  <!-- The clinician's overall risk rating (this field belongs to the presenting-problem group) -->
  <div [formGroup]="group()">
    <h2 class="mb-1 text-lg font-semibold text-slate-900">Risk rating</h2>
    <p class="mb-4 text-sm text-slate-500">The clinician's rating of the risk to the member and to others.</p>
    <scale-slider label="Risk rating" formControlName="riskRatingId" [options]="options('riskRatings')" [showDescription]="true" />
  </div>
</section>
